import { useCallback, useEffect, useState } from 'react';

import { HealthMarker } from '../types/Bloodwork';
import { HealthMarkerOverviewReportListItem, HealthMarkerReportListItem } from '../types/HealthMarkerReportList';
import HealthMarkerService from 'src/api/healthMarkers';
import { useAnalytics } from './useAnalytics';
import { useAppStore } from '../store';

type UseOverviewAssessments = (
  refreshing: boolean,
  finishRefreshing: () => void
) => [Map<string, HealthMarkerReportListItem[]>, string[], number, boolean, any];

export const useOverViewAssessments: UseOverviewAssessments = (refreshing, finishRefreshing) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [assessmentsGroup, setAssessmentsGroup] = useState<Map<string, HealthMarkerReportListItem[]>>(new Map());
  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [totalAssessments, setTotalAssessments] = useState<number>(0);
  const [error, setError] = useState<any>();

  const { addAnalyticsLog } = useAnalytics('useAssessments.ts');

  const getOverviewAssessments = useCallback(
    (promise: Promise<HealthMarkerOverviewReportListItem[]>) => {
      setLoading(true);
      promise
        .then((healthMarkerList) => {
          const group = new Map<string, HealthMarkerReportListItem[]>();
          let numberOfAssessments = 0;
          for (const item of healthMarkerList) {
            numberOfAssessments += item.totalAssessments;
            group.set(
              String(item.assessmentId),
              item.latestAssessments.map((report) => {
                return { ...report, numberOfAssesments: item.totalAssessments };
              })
            );
          }

          setAssessmentsGroup(group);
          setGroupNames([...group.keys()]);
          setTotalAssessments(numberOfAssessments);
        })
        .catch((err) => {
          addAnalyticsLog({ data: err, function: 'getOverviewAssessments', logType: 'error' });
          if (err?.error?.name != 'CanceledError' && err?.isAborted == false) {
            setError(err);
          }
        })
        .finally(() => {
          setLoading(false);
          finishRefreshing();
        });
    },
    [addAnalyticsLog, finishRefreshing]
  );

  useEffect(() => {
    if (assessmentsGroup.size === 0) {
      const { promise, abort } = HealthMarkerService.getOverviewUserAssessments();
      getOverviewAssessments(promise);
      return abort;
    }
  }, [assessmentsGroup.size, getOverviewAssessments]);

  useEffect(() => {
    if (refreshing) {
      const { promise, abort } = HealthMarkerService.getOverviewUserAssessments();
      getOverviewAssessments(promise);
      return abort;
    }
  }, [getOverviewAssessments, refreshing]);

  return [assessmentsGroup, groupNames, totalAssessments, loading, error];
};

type UseAssessments = (
  refreshing: boolean,
  finishRefreshing: () => void,
  take: number,
  skip: number,
  assessmentType: string
) => [HealthMarkerReportListItem[], boolean, any];

export const useAssessments: UseAssessments = (refreshing, finishRefreshing, take, skip, assessmentType) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [assessments, setAssessments] = useState<HealthMarkerReportListItem[]>([]);

  const [error, setError] = useState<any>();
  const { addAnalyticsLog } = useAnalytics('useAssessments.ts');

  const getAssessments = useCallback(
    (promise: Promise<HealthMarkerReportListItem[]>) => {
      setLoading(true);
      promise
        .then((healthMarkerList) => {
          setAssessments(healthMarkerList);
        })
        .catch((err) => {
          addAnalyticsLog({ data: err, function: 'useEffect', logType: 'error' });
          setError(err);
        })
        .finally(() => {
          setLoading(false);
          finishRefreshing();
        });
    },
    [addAnalyticsLog, finishRefreshing]
  );

  useEffect(() => {
    if (refreshing && loading == false) {
      const { promise } = HealthMarkerService.getUserAssessments(assessmentType, take, skip);
      getAssessments(promise);
    }
  }, [getAssessments, refreshing, take, skip, assessmentType, loading]);

  return [assessments, loading, error];
};

export const useGetPendingBloodwork = (refreshing: boolean) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [documentId, setDocumentId] = useState<string>();

  const { addAnalyticsLog } = useAnalytics('useAssessments.ts');

  const getPendingBloodwork = useCallback(
    (promise: Promise<string>) => {
      setLoading(true);
      promise
        .then((bloodworkId: string) => {
          setDocumentId(bloodworkId);
        })
        .catch((err) => {
          addAnalyticsLog({ function: 'getPendingBloodWork', data: err, logType: 'error' });
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [addAnalyticsLog]
  );

  useEffect(() => {
    if (!documentId && documentId !== '') {
      const { promise, abort } = HealthMarkerService.getPendingBloodwork();
      getPendingBloodwork(promise);
      return abort;
    }
  }, [documentId, getPendingBloodwork]);

  useEffect(() => {
    if (refreshing) {
      const { promise, abort } = HealthMarkerService.getPendingBloodwork();
      getPendingBloodwork(promise);
      return abort;
    }
  }, [getPendingBloodwork, refreshing]);

  return [documentId, loading, error];
};

export const useLoadHealthMarkerUnits = () => {
  const { addAnalyticsLog } = useAnalytics('useAssessments.ts');

  const healthMarkers = useAppStore((state) => state.healthMarkers);
  const setHealthMarkers = useAppStore((state) => state.setHealthMarkers);

  const loadHealthMarkers = useCallback(
    (promise: Promise<HealthMarker[]>) => {
      promise
        .then((units) => {
          setHealthMarkers(units.filter((unit: HealthMarker) => unit.healthMarkerName));
        })
        .catch((err) => {
          addAnalyticsLog({ function: 'getHealthMarkersAndUnits', data: err, logType: 'error' });
        });
    },
    [addAnalyticsLog, setHealthMarkers]
  );

  useEffect(() => {
    if (healthMarkers.length === 0) {
      const { promise, abort } = HealthMarkerService.getUnitDetails();
      loadHealthMarkers(promise);
      return abort;
    }
  }, [healthMarkers.length, loadHealthMarkers]);
};
